import {RefObject, useEffect, useRef, useState} from 'react';
import './HolmesJrGuide.css';
import {useHolmesThinking} from './holmesThinkingStore';

/**
 * Idle-pose wardrobe, keyed by screen context. Only 'guide' has a real asset
 * today (profenz.com/brand/holmes-jr.png); the rest are placeholders pointing
 * at the same image until dedicated art exists — see
 * docs/ai/HOLMES_JR_POSE_BRIEF.md for the requested pose briefs
 * (reading-glasses transition frame, administrator desk/accounting-cap pose,
 * serious vs. friendly expression variants). Swapping in real art later is a
 * one-line change per entry, no component change needed.
 */
export type HolmesPose = 'guide' | 'administrator';

const POSE_ASSET: Record<HolmesPose, string> = {
  guide: 'https://profenz.com/brand/holmes-jr.png',
  administrator: 'https://profenz.com/brand/holmes-jr.png',
};

/** The "thinking" state's real brand art -- a glass sphere of branching electric-blue
 * neural lines with legal iconography suspended inside. Cropped into the round avatar
 * frame the same way the guide poses are (see .hjr-sphere in HolmesJrGuide.css). */
const THINKING_SPHERE_ASSET = 'https://profenz.com/brand/holmes-thinking.png';

/** Floor so a fast synthetic resolve still reads as a deliberate thought, not a flicker. */
const MIN_VISIBLE_THINKING_MS = 550;

type Props = {
  pose?: HolmesPose;
  /**
   * A short, contextual explanation of what's happening on this screen right
   * now and what to do next — e.g. "You have 2 attempts awaiting review."
   * Not a chat/AI feature: each screen computes this from its own current
   * state and passes it in as plain text. Hidden while thinking, since the
   * "Thinking…" label already communicates activity.
   */
  tip?: string;
  onActivate?: () => void;
  launchRef?: RefObject<HTMLButtonElement | null>;
};

export function HolmesJrGuide({pose = 'guide', tip, onActivate, launchRef}: Props) {
  const isThinking = useHolmesThinking();
  const [showThinking, setShowThinking] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (isThinking) {
      shownAtRef.current = Date.now();
      setShowThinking(true);
      return;
    }
    if (shownAtRef.current === null) {
      setShowThinking(false);
      return;
    }
    const elapsed = Date.now() - shownAtRef.current;
    const remaining = Math.max(0, MIN_VISIBLE_THINKING_MS - elapsed);
    const timer = setTimeout(() => {
      setShowThinking(false);
      shownAtRef.current = null;
    }, remaining);
    return () => clearTimeout(timer);
  }, [isThinking]);

  const avatar = <>
    <img src={POSE_ASSET[pose]} alt="" aria-hidden="true" className="hjr-avatar" />
    <img src={THINKING_SPHERE_ASSET} alt="" aria-hidden="true" className="hjr-sphere" />
  </>;

  return (
    <div className={`hjr-root${showThinking ? ' is-thinking' : ''}`}>
      {tip && !showThinking && (
        <p className="hjr-tip" role="note">
          {tip}
        </p>
      )}
      <div className="hjr-row" role="status" aria-live="polite">
        <span className="hjr-label">{showThinking ? 'Thinking…' : 'Holmes Jr.'}</span>
        {onActivate
          ? <button ref={launchRef} className="hjr-avatar-wrap hjr-launch" type="button" onClick={onActivate} aria-label="Ask Holmes Jr." title="Ask Holmes Jr. about this page">{avatar}</button>
          : <div className="hjr-avatar-wrap">{avatar}</div>}
      </div>
    </div>
  );
}
